/* Souq Waffar: lightweight, same-browser recently viewed products. */
(() => {
  'use strict';
  if (window.__swRecentlyViewedV2) return;
  window.__swRecentlyViewedV2 = true;
  const storageKey = 'sw:recent-product-handles:v1';
  const maxHistory = 12;
  const maxAge = 30 * 24 * 60 * 60 * 1000;
  const processed = new WeakSet();
  const validHandle = h => typeof h === 'string' && /^[a-z0-9][a-z0-9-]{0,255}$/.test(h);
  const now = () => Date.now();

  function readHistory() {
    try {
      const entries = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (!Array.isArray(entries)) return [];
      const seen = new Set();
      const clock = now();
      return entries.filter(item => item && validHandle(item.h) &&
        Number.isFinite(item.t) && item.t <= clock && clock - item.t < maxAge &&
        !seen.has(item.h) && (seen.add(item.h), true)).slice(0, maxHistory);
    } catch { return []; }
  }

  function recordView(handle, history) {
    if (!validHandle(handle)) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify([
        { h: handle, t: now() }, ...history.filter(item => item.h !== handle)
      ].slice(0, maxHistory)));
    } catch { /* Private browsing or disabled storage: no persistent history. */ }
  }

  const preview = new URLSearchParams(location.search).get('preview_theme_id');
  const previewTheme = preview && /^\d+$/.test(preview) ? preview : '';
  const cache = new Map();
  const clearedSections = new WeakSet();

  function productSectionUrl(root, handle) {
    const url = new URL(root.replace(/\/?$/, '/') + 'products/' + encodeURIComponent(handle), location.origin);
    url.searchParams.set('section_id', 'sw-recently-viewed-card');
    if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
    return url;
  }

  async function getCard(root, handle) {
    const key = root + ':' + handle;
    if (!cache.has(key)) {
      cache.set(key, (async () => {
        try {
          const res = await fetch(productSectionUrl(root, handle), { credentials: 'same-origin' });
          if (!res.ok) return null;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const card = doc.querySelector('[data-sw-recent-card]');
          const anchors = card?.querySelectorAll('a[href]');
          if (!card || !anchors?.length) return null;
          if (previewTheme) {
            for (const anchor of anchors) {
              const target = new URL(anchor.href, location.origin);
              if (target.origin !== location.origin) return null;
              target.searchParams.set('preview_theme_id', previewTheme);
              anchor.href = target.toString();
            }
          }
          return card;
        } catch { return null; }
      })());
    }
    return cache.get(key);
  }

  function init(section) {
    if (processed.has(section)) return;
    processed.add(section);
    const current = section.dataset.productHandle;
    const history = readHistory();
    recordView(current, history);
    const entries = history.filter(item => item.h !== current);
    if (!entries.length) return;

    const grid = section.querySelector('[data-sw-recent-grid]');
    if (!grid) return;
    const root = section.dataset.routeRoot || '/';
    const limit = Math.min(6, Math.max(2, Number(section.dataset.maxProducts) || 4));
    const showCards = async () => {
      // Skip deleted, unavailable or inaccessible products without leaving empty slots.
      // Fetch the first relevant candidates together, and request later candidates only if needed.
      for (let offset = 0; offset < entries.length && grid.children.length < limit; offset += limit) {
        const batch = await Promise.all(entries.slice(offset, offset + limit).map(item => getCard(root, item.h)));
        if (!section.isConnected || clearedSections.has(section)) return;
        for (const element of batch) {
          if (element && grid.children.length < limit) grid.append(element.cloneNode(true));
        }
        if (grid.children.length && !clearedSections.has(section)) section.hidden = false;
      }
    };

    const sentinel = section.previousElementSibling;
    if ('IntersectionObserver' in window && sentinel?.matches('[data-sw-recent-sentinel]')) {
      const observer = new IntersectionObserver(items => {
        if (!items.some(item => item.isIntersecting)) return;
        observer.disconnect();
        void showCards();
      }, { rootMargin: '500px 0px', threshold: 0 });
      observer.observe(sentinel);
    } else {
      void showCards();
    }
  }

  function clearVisitedHistory() {
    try { localStorage.removeItem(storageKey); } catch { /* Storage disabled. */ }
    document.querySelectorAll('[data-sw-recently-viewed]').forEach(section => {
      clearedSections.add(section);
      const grid = section.querySelector('[data-sw-recent-grid]');
      if (grid) grid.replaceChildren();
      section.hidden = true;
    });
  }

  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-sw-recent-clear]');
    if (button) clearVisitedHistory();
  });

  function boot(root = document) {
    root.querySelectorAll('[data-sw-recently-viewed]').forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', event => boot(event.target));
})();
